import React, {useState} from 'react';
import {observer} from 'mobx-react';
import {useIntl} from 'react-intl';
import {FormGroup, Input, Label} from 'reactstrap';
import {normalize, useEntityOptions} from './entities';
import styles from './billing-block.module.scss';

// ATALHO: shows at most 50 matches; switch to a virtualized list if centrals reach thousands of devices.
const MAX_OPTIONS = 50;

interface EntitySearchFieldProps {
  id: string;
  kind: 'customer' | 'device';
  label: string;
  value: number | null;
  onChange: (id: number | null) => void;
  exclude?: number[];
}

const EntitySearchField = ({id, kind, label, value, onChange, exclude = []}: EntitySearchFieldProps) => {
  const intl = useIntl();
  const entities = useEntityOptions();
  const options = kind === 'customer' ? entities.customers : entities.devices;
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const selected = options.find((option) => option.id === value);
  const query = normalize(text.trim());
  const matches = open && query ?
    options.filter((option) => !exclude.includes(option.id) && option.search.includes(query)).slice(0, MAX_OPTIONS) :
    [];
  const listId = `${id}-options`;
  const optionId = (index: number) => `${id}-option-${index}`;

  const choose = (optionValue: number | null) => {
    onChange(optionValue);
    setOpen(false);
    setText('');
    setActive(-1);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive((current) => Math.min(current + 1, matches.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter' && open && matches[active]) {
      event.preventDefault();
      choose(matches[active].id);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
      setText('');
    }
  };

  return (
    <FormGroup className="custom-formGroup">
      <Label for={id}>{label}</Label>
      <div className={styles.searchControl}>
        <Input
          id={id}
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={matches.length > 0}
          aria-controls={listId}
          aria-activedescendant={matches[active] ? optionId(active) : undefined}
          placeholder={intl.formatMessage({id: 'billingBlockSearchPlaceholder'})}
          value={open ? text : selected?.label || ''}
          onChange={(event) => {
            setText(event.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            setOpen(false);
            setText('');
          }}
        />
        {value !== null && (
          <button
            type="button"
            className={styles.clearButton}
            aria-label={intl.formatMessage({id: 'billingBlockSearchClear'}, {field: label})}
            onClick={() => choose(null)}
          >
            ×
          </button>
        )}
      </div>
      {matches.length > 0 && (
        <ul id={listId} role="listbox" className={styles.searchOptions} aria-label={label}>
          {matches.map((option, index) => (
            <li
              key={option.id}
              id={optionId(index)}
              role="option"
              aria-selected={index === active}
              onMouseDown={(event) => {
                event.preventDefault();
                choose(option.id);
              }}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
      {open && query && matches.length === 0 && (
        <p role="status" className={styles.hint}>{intl.formatMessage({id: 'billingBlockSearchNoResults'})}</p>
      )}
    </FormGroup>
  );
};

export default observer(EntitySearchField);
